import React, { useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import { Box, Paper, Typography, TextField, Grid, Button } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";

import InsuredCamera from "../components/InsuredCamera";
import SurveyorCamera from "../components/SurveyorCamera";
import MediaGallery from "../components/MediaGallery";
import { usePeerConnection } from "../hooks/usePeerConnection";
import { useMediaCapture } from "../hooks/useMediaCapture";

export default function Surveyor() {
  // ✅ KEEP USING AID (ROOM ID)
  const { aid } = useParams();
  const roomId = aid;


  const navigate = useNavigate();

  const localVideoRef = useRef(null);
  const socketRef = useRef(null);

  const [sessionValid, setSessionValid] = useState(false);
  const [localStream, setLocalStream] = useState(null);

  const [openGallery, setOpenGallery] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const [mode, setMode] = useState("photo");
  const [videoStyle, setVideoStyle] = useState({});

  const [videos, setVideos] = useState([]);

  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);

  const [isReconnecting, setIsReconnecting] = useState(false);
  const reconnectStartRef = useRef(null);

  const [mobileNumber, setMobileNumber] = useState("");
  const [sending, setSending] = useState(false);  

  const [insuredInfo, setInsuredInfo] = useState({
    lat: null,
    lng: null,
    time: null,
    address: null,
  });

//   useEffect(() => {
//   console.log("📡 FINAL insuredInfo STATE:", insuredInfo);
// }, [insuredInfo]);

  /* ---------------- SOCKET (ROOM ONLY) ---------------- */
  useEffect(() => {
    if (!roomId) return;

    console.log("🚀 Initializing surveyor socket for room:", roomId);

    const socket = io("https://live.claimsmitra.com", {
      path: "/socket.io",
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      transports: ["websocket"],
    });

    socketRef.current = socket;

    /* ---------------- CONNECT ---------------- */
    socket.on("connect", () => {
      console.log("✅ SOCKET CONNECTED");
      console.log("🆔 Socket ID:", socket.id);

      // JOIN ROOM
      socket.emit("join-room", {
        roomId: String(roomId),
        role: "surveyor",
      });

      // RESUME TIMER
      if (isReconnecting && reconnectStartRef.current) {

        const disconnectedFor =
          Math.floor((Date.now() - reconnectStartRef.current) / 1000);

        console.log("⏱ Disconnected for:", disconnectedFor, "sec");

        // OPTIONAL:
        // subtract paused duration from recording timer here

        reconnectStartRef.current = null;
      }

      setIsReconnecting(false);

      console.log("📦 join-room emitted:", roomId);
    });

    /* ---------------- ROOM JOINED ACK ---------------- */
    socket.on("room-joined", (data) => {
      console.log("✅ ROOM JOINED:", data);
    });

    socket.on("room-update", (data) => {
      console.log("👥 ROOM UPDATE:", data);
    });

    /* ---------------- SURVEY COMPLETED ---------------- */
    socket.on("survey-completed", () => {

      console.log("✅ Survey completed");

      alert("Survey has been completed.");

      cleanupConnections();

      navigate("/");
    });

    /* ---------------- INSURED STATUS ---------------- */
    socket.on("insured-status", (data) => {

      console.log("📥 insured-status RECEIVED:", data);

      if (!data) {
        console.log("❌ Empty insured-status payload");
        return;
      }

      // ✅ PRINT LOCATION CLEARLY
      console.log("📍 INSURED LOCATION");
      console.log("Latitude:", data.lat);
      console.log("Longitude:", data.lng);
      console.log("Time:", data.time);
      console.log("Address:", data.address);

      // ✅ OPTIONAL GOOGLE MAP LINK
      if (data.lat && data.lng) {
        console.log(
          `🌍 MAP: https://www.google.com/maps?q=${data.lat},${data.lng}`
        );
      }

      setInsuredInfo((prev) => ({
        ...prev,
        lat: data.lat ?? prev.lat,
        lng: data.lng ?? prev.lng,
        time: data.time ?? prev.time,
        address: data.address ?? prev.address,
      }));

    });

    /* ---------------- ERROR ---------------- */
    socket.on("connect_error", (err) => {
      console.log("❌ SOCKET CONNECT ERROR:", err.message);
    });

    socket.on("error", (err) => {
      console.log("❌ SOCKET ERROR:", err);
    });

    /* ---------------- DISCONNECT ---------------- */
    socket.on("disconnect", (reason) => {
      console.log("❌ SOCKET DISCONNECTED:", reason);
      reconnectStartRef.current = Date.now();
      setIsReconnecting(true);
    });

    /* ---------------- RECONNECTING ---------------- */
    socket.io.on("reconnect_attempt", () => {

      console.log("🔄 Reconnecting...");

      setIsReconnecting(true);
    });

    /* ---------------- RECONNECTED ---------------- */
    socket.io.on("reconnect", () => {

      console.log("✅ Reconnected");

      setIsReconnecting(false);

      socket.emit("join-room", {
        roomId: String(roomId),
        role: "surveyor",
      });
    });

    return () => {
      console.log("🛑 Cleaning socket");
      socket.disconnect();
    };

  }, [roomId]);

  /* ---------------- CAMERA ---------------- */
  useEffect(() => {
    if (!roomId) return;

    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 3840 },
            height: { ideal: 2160 },
            frameRate: { ideal: 30 },
            facingMode: "environment",
          },
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });

        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }

        setLocalStream(stream);
        setSessionValid(true);
      } catch (err) {
        console.error("Camera error:", err);
      }
    };

    startCamera();
  }, [roomId]);


  /* ---------------- SEND WHATSAPP ---------------- */
  const handleSendWhatsapp = async () => {

  if (!mobileNumber) {
    alert("Please enter mobile number");
    return;
  }

  try {

    setSending(true);

    const payload = {
      to: mobileNumber,
      template_name: "live_survey",
      language_code: "en_US",
      support_number: "+918619589872",
      case_reference: roomId,
    };

    console.log("📤 Sending Payload:", payload);

    const response = await fetch(
      "https://claimsmitra.com/send-whatsapp",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    console.log("📡 HTTP STATUS:", response.status);

    const text = await response.text();

    console.log("📥 RAW RESPONSE:", text);

    let data = {};

    try {
      data = JSON.parse(text);
    } catch (e) {
      console.log("❌ JSON Parse Error");
    }

    if (response.ok) {

      alert("WhatsApp message sent successfully");

    } else {

      alert(
        data?.message ||
        data?.error?.message ||
        "Failed to send WhatsApp message"
      );
    }

  } catch (err) {

    console.error("❌ FETCH ERROR:", err);

    alert(
      "API request failed. Check console/network tab."
    );

  } finally {

    setSending(false);
  }
};

  /* ---------------- PEER CONNECTION ---------------- */
  const {
    remoteVideoRef,
    peerId,
    connected,
    address,
    peerInstance,
    currentCall,
  } = usePeerConnection(sessionValid ? roomId : null, localStream, setInsuredInfo);

  /* ---------------- MEDIA CAPTURE ---------------- */
  const {
    capturedImages,
    recordedVideos,
    recording,
    paused,
    progress,
    setCapturedImages,
    setRecordedVideos,
    handleCapture,
    handleVideoRecord,
    pauseRecording,
    resumeRecording,
    stopRecording,
  } = useMediaCapture(remoteVideoRef, insuredInfo, address, roomId, isReconnecting);

  /* ---------------- CLEANUP ---------------- */
  const cleanupConnections = () => {

    console.log("🛑 Cleaning all connections");

    // STOP LOCAL STREAM
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
    }

    // CLOSE PEER CALL
    if (currentCall) {
      currentCall.close();
    }

    // DESTROY PEER
    if (peerInstance) {
      peerInstance.destroy();
    }

    // DISCONNECT SOCKET
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
  };
  /* ---------------- COMPLETE SURVEY ---------------- */
  const handleCompleteSurvey = () => {

    const confirmComplete = window.confirm(
      "Are you sure you want to complete this survey?"
    );

    if (!confirmComplete) return;

    // INFORM INSURED
    socketRef.current?.emit("complete-survey", {
      roomId,
    });

    cleanupConnections();

    alert("Survey completed successfully.");

    navigate("/");
  };


  /* ---------------- CONTROLS ---------------- */
  const toggleMute = () => {

    if (!localStream) return;

    const audioTracks = localStream.getAudioTracks();

    if (!audioTracks.length) return;

    const newMutedState = !isMuted;

    audioTracks.forEach((track) => {
      track.enabled = !newMutedState;
    });

    setIsMuted(newMutedState);
  };

  const toggleVideo = () => {

    if (!localStream) return;

    const videoTracks = localStream.getVideoTracks();

    if (!videoTracks.length) return;

    const newVideoState = !isVideoOff;

    videoTracks.forEach((track) => {
      track.enabled = !newVideoState;
    });

    setIsVideoOff(newVideoState);
  };
  return (
    <Box sx={{ height: "100vh", p: 2 }}>
      <Box sx={{ display: "flex", gap: 2, height: "85%" }}>
        
        {/* INSURED VIEW */}
        <InsuredCamera
          connected={connected}
          handleCapture={handleCapture}
          startRecording={handleVideoRecord}
          pauseRecording={pauseRecording}
          resumeRecording={resumeRecording}
          stopRecording={stopRecording}
          recording={recording}
          paused={paused}
          progress={progress}
          mode={mode}
          setMode={setMode}
          remoteVideoRef={remoteVideoRef}
          surveyorVideoRef={remoteVideoRef}   // ✅ FIXED
          insuredInfo={insuredInfo}
          address={address}
          videoStyle={videoStyle}
          peerId={roomId}
          isReconnecting={isReconnecting}
        />

        {/* SURVEYOR VIEW */}
        <SurveyorCamera
          localVideoRef={localVideoRef}
          videos={videos}
          recordedVideos={recordedVideos}
          capturedImages={capturedImages}
          isMuted={isMuted}
          isVideoOff={isVideoOff}
          toggleMute={toggleMute}
          toggleVideo={toggleVideo}
          setOpenGallery={setOpenGallery}
        />
      </Box>

      {/* ✅ CONTROL PANEL (ROOM ONLY) */}
      <Paper sx={{ mt: 2, p: 2 }}>
        <Grid container spacing={2}>

          <Grid item xs={3}>
            <Typography>Room ID</Typography>
            <TextField fullWidth value={roomId || ""} />
          </Grid>

          

          <Grid item xs={3}>
            <Typography>Share Link</Typography>
            <TextField
              fullWidth
              value={
                roomId
                  ? `${window.location.origin}/precheck/${roomId}`
                  : ""
              }
            />
          </Grid>

          <Grid item xs={3}>
            <Typography>Mobile Number</Typography>
              <TextField
                fullWidth
                placeholder="Enter mobile number"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
              />
          </Grid>

          <Grid item xs={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSendWhatsapp}
              disabled={sending}
              sx={{
                height: "56px",
                mt: "22px",
              }}
            >
              {sending ? "Sending..." : "Send"}
            </Button>
          </Grid>


        </Grid>
      </Paper>

      {/* COMPLETE SURVEY BUTTON */}
      <Button
        variant="contained"
        color="error"
        onClick={handleCompleteSurvey}
        sx={{
          position: "fixed",
          bottom: 20,
          right: 20,
          zIndex: 9999,
          px: 4,
          py: 1.5,
          borderRadius: "12px",
          fontWeight: 600,
        }}
      >
        Complete Survey
      </Button>

      {/* MEDIA GALLERY */}
      <MediaGallery
        open={openGallery}
        setOpen={setOpenGallery}
        images={capturedImages}
        videos={[...(recordedVideos || []), ...(videos || [])]}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        recordedVideos={recordedVideos}
        peerId={roomId}
        onRemoveImage={(index) =>
          setCapturedImages((prev) => prev.filter((_, i) => i !== index))
        }
        onRemoveVideo={(index) => {
          if (index < recordedVideos.length) {
            setRecordedVideos((prev) => prev.filter((_, i) => i !== index));
          } else {
            const adjustedIndex = index - recordedVideos.length;
            setVideos((prev) => prev.filter((_, i) => i !== adjustedIndex));
          }
        }}
      />
    </Box>
  );
}